import type { SceneObject } from "./SceneHost";
import { clonePoints, type PathPoint } from "./pathGeometry";
import { syncSegmentOnGroup } from "./render/renderers/segment";
import {
  isSegmentObject,
  segmentLength,
  snapSegmentEnd,
} from "./segmentGeometry";
import { snapPointToBindable } from "./bindings/bindingSnap";
import type { SegmentBindings } from "@bezpart-board/shared";

const MIN_LEN_PX = 6;

type SegmentPointHost = {
  canEdit: boolean;
  getActiveTool(): string;
  getSelection(): string[];
  getObjects(): SceneObject[];
  findObject(id: string): SceneObject | null;
  getObjectElement(id: string): Element | null;
  stagePoint(clientX: number, clientY: number): { x: number; y: number };
  getSvgDefs(): SVGDefsElement | undefined;
  commitBatchPatches(
    patches: { id: string; patch: Record<string, unknown> }[],
    clientOpId: string,
    undoEntry?: {
      forward: { type: "update_object"; id: string; patch: Record<string, unknown> }[];
      inverse: { type: "update_object"; id: string; patch: Record<string, unknown> }[];
    }
  ): Promise<void>;
  onLayoutChange?: () => void;
};

type DragState = {
  objectId: string;
  pointIndex: 0 | 1;
  originPoints: [PathPoint, PathPoint];
  originPatch: { x: number; y: number; w: number; h: number };
  originBindings?: SegmentBindings;
  moved: boolean;
};

export type SegmentPointHandlesController = {
  update: () => void;
  destroy: () => void;
};

function positionHandle(el: HTMLElement, x: number, y: number) {
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
}

export function createSegmentPointHandles(
  overlay: HTMLElement,
  host: SegmentPointHost
): SegmentPointHandlesController {
  const root = document.createElement("div");
  root.className = "wb-segment-point-handles wb-hidden";
  root.setAttribute("aria-hidden", "true");

  const handleA = document.createElement("div");
  handleA.className = "wb-segment-point-handle";
  handleA.dataset.pointIndex = "0";
  handleA.title = "Конец линии";

  const handleB = document.createElement("div");
  handleB.className = "wb-segment-point-handle";
  handleB.dataset.pointIndex = "1";
  handleB.title = "Конец линии";

  root.appendChild(handleA);
  root.appendChild(handleB);
  overlay.appendChild(root);

  let drag: DragState | null = null;

  function syncSegmentDom(obj: SceneObject) {
    const el = host.getObjectElement(obj.id);
    if (!(el instanceof SVGGElement)) return;
    syncSegmentOnGroup(el, obj, {
      defs: host.getSvgDefs(),
      markerEnd: obj.type === "arrow",
    });
  }

  function update() {
    if (!host.canEdit || host.getActiveTool() !== "select") {
      root.classList.add("wb-hidden");
      return;
    }
    const ids = host.getSelection();
    if (ids.length !== 1) {
      root.classList.add("wb-hidden");
      return;
    }
    const obj = host.findObject(ids[0]!);
    if (!obj || !isSegmentObject(obj) || obj.locked) {
      root.classList.add("wb-hidden");
      return;
    }
    const a = obj.points[0]!;
    const b = obj.points[1]!;
    root.classList.remove("wb-hidden");
    positionHandle(handleA, a[0], a[1]);
    positionHandle(handleB, b[0], b[1]);
    handleA.classList.toggle("is-bound", Boolean(obj.bindings?.start?.objectId));
    handleB.classList.toggle("is-bound", Boolean(obj.bindings?.end?.objectId));
    host.onLayoutChange?.();
  }

  function onHandleDown(ev: MouseEvent) {
    if (!host.canEdit || ev.button !== 0) return;
    const index = Number((ev.currentTarget as HTMLElement).dataset.pointIndex);
    if (index !== 0 && index !== 1) return;
    ev.preventDefault();
    ev.stopPropagation();

    const ids = host.getSelection();
    if (ids.length !== 1) return;
    const obj = host.findObject(ids[0]!);
    if (!obj || !isSegmentObject(obj) || obj.locked) return;

    const a = obj.points[0]!;
    const b = obj.points[1]!;
    drag = {
      objectId: obj.id,
      pointIndex: index as 0 | 1,
      originPoints: [
        [a[0], a[1]],
        [b[0], b[1]],
      ],
      originPatch: {
        x: obj.x,
        y: obj.y,
        w: typeof obj.w === "number" ? obj.w : 1,
        h: typeof obj.h === "number" ? obj.h : 1,
      },
      originBindings: obj.bindings
        ? (JSON.parse(JSON.stringify(obj.bindings)) as SegmentBindings)
        : undefined,
      moved: false,
    };

    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup", onDragUp);
  }

  function onDragMove(ev: MouseEvent) {
    if (!drag) return;
    const obj = host.findObject(drag.objectId);
    if (!obj || !isSegmentObject(obj)) return;

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const anchor = drag.pointIndex === 0 ? drag.originPoints[1]! : drag.originPoints[0]!;
    let next: PathPoint = [Math.round(pt.x), Math.round(pt.y)];
    if (ev.shiftKey) {
      next = snapSegmentEnd(anchor, next, true);
    }

    const nextPoints: [PathPoint, PathPoint] =
      drag.pointIndex === 0
        ? [next, [drag.originPoints[1]![0], drag.originPoints[1]![1]]]
        : [[drag.originPoints[0]![0], drag.originPoints[0]![1]], next];

    if (segmentLength(nextPoints[0], nextPoints[1]) < MIN_LEN_PX) return;

    drag.moved = true;
    obj.points = nextPoints;
    syncSegmentDom(obj);
    update();
  }

  function endDrag(commit: boolean) {
    if (!drag) return;
    const state = drag;
    drag = null;
    document.removeEventListener("mousemove", onDragMove);
    document.removeEventListener("mouseup", onDragUp);

    const obj = host.findObject(state.objectId);
    if (!obj || !isSegmentObject(obj)) {
      update();
      return;
    }

    if (!commit || !state.moved) {
      obj.points = clonePoints(state.originPoints);
      if (state.originBindings !== undefined) {
        obj.bindings = state.originBindings;
      }
      syncSegmentDom(obj);
      update();
      return;
    }

    const a = obj.points[0]!;
    const b = obj.points[1]!;
    if (segmentLength(a, b) < MIN_LEN_PX) {
      obj.points = clonePoints(state.originPoints);
      syncSegmentDom(obj);
      update();
      return;
    }

    const patch: Record<string, unknown> = {
      x: obj.x,
      y: obj.y,
      w: obj.w,
      h: obj.h,
      points: clonePoints(obj.points),
    };
    const invPatch: Record<string, unknown> = {
      x: state.originPatch.x,
      y: state.originPatch.y,
      w: state.originPatch.w,
      h: state.originPatch.h,
      points: clonePoints(state.originPoints),
    };

    const endpointKey = state.pointIndex === 0 ? "start" : "end";
    const otherPoint =
      state.pointIndex === 0 ? state.originPoints[1]! : state.originPoints[0]!;
    const draggedPoint = obj.points[state.pointIndex]!;
    const exclude = new Set([state.objectId]);
    const snap = snapPointToBindable(
      host.getObjects(),
      [draggedPoint[0], draggedPoint[1]],
      otherPoint,
      exclude
    );

    const nextBindings: SegmentBindings = {
      ...(state.originBindings ?? {}),
    };
    if (snap) {
      obj.points[state.pointIndex] = snap.point;
      patch.points = clonePoints(obj.points);
      nextBindings[endpointKey] = snap.binding;
      patch.bindings = nextBindings;
    } else if (state.originBindings?.[endpointKey]) {
      nextBindings[endpointKey] = null;
      const hasAny = nextBindings.start?.objectId || nextBindings.end?.objectId;
      patch.bindings = hasAny ? nextBindings : null;
    }

    if (state.originBindings !== undefined) {
      invPatch.bindings = state.originBindings ?? null;
    }

    void host
      .commitBatchPatches([{ id: state.objectId, patch }], `segment-point-${Date.now()}`, {
        forward: [{ type: "update_object", id: state.objectId, patch }],
        inverse: [{ type: "update_object", id: state.objectId, patch: invPatch }],
      })
      .finally(() => update());
  }

  function onDragUp() {
    endDrag(true);
  }

  handleA.addEventListener("mousedown", onHandleDown);
  handleB.addEventListener("mousedown", onHandleDown);

  return {
    update,
    destroy() {
      endDrag(false);
      handleA.removeEventListener("mousedown", onHandleDown);
      handleB.removeEventListener("mousedown", onHandleDown);
      root.remove();
    },
  };
}
